import { EnvironmentInjector, Injectable, inject } from '@angular/core';
import { fromJson, toJson } from '@bufbuild/protobuf';
import { NbToastrService } from '@nebular/theme';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  CollectionNames,
  ProjectJobServiceCreateResponseJson,
  ProjectJobServiceCreateResponseSchema,
  ProjectJobServiceListRequestJson,
  ProjectJobServiceListRequestSchema,
  defaultApiSerializeJsonOptions,
} from '@vmspdb/definitions';
import { GlobalErrorHandlerService } from '@vmspdb/ui/common';
import { AuthRunEffects } from '@vmspdb/ui/store';
import { EMPTY, Observable, of, tap } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import {
  ProjectJobRepositoryService,
  withSearchSync,
} from '../../repositories';
import { UtilitiesFacade } from '../utilities/utilities.facade';
import { PaginatedProjectJobActions } from './paginated-project-job.actions';

@Injectable()
export class PaginatedProjectJobEffects extends AuthRunEffects {
  private readonly injector = inject(EnvironmentInjector);
  private readonly errors = inject(GlobalErrorHandlerService);
  private readonly repository = inject(ProjectJobRepositoryService);
  private readonly notifications = inject(NbToastrService);
  private readonly utilities = inject(UtilitiesFacade);

  listPaginatedProjectJobs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        PaginatedProjectJobActions.listPaginatedProjectJobs,
        PaginatedProjectJobActions.cancel,
      ),
      switchMap((action) =>
        action.type === PaginatedProjectJobActions.cancel.type
          ? EMPTY
          : of(action).pipe(this.performSearch()),
      ),
    );
  });

  performSearch() {
    return (
      source: Observable<{
        params: ProjectJobServiceListRequestJson;
      }>,
    ) =>
      source.pipe(
        debounceTime(250),
        withSearchSync(CollectionNames.ProjectJobs, this.injector),
        tap(() => this.utilities.queue()),
        switchMap((payload) => {
          return this.repository.list(
            fromJson(ProjectJobServiceListRequestSchema, payload.params),
          );
        }),
        tap((data) => {
          if (data.degraded) {
            this.errors.reportSearchDegradation(CollectionNames.ProjectJobs);
            this.notifications.warning(
              'There was an error with the search server for fetching project jobs.',
              'Search',
              {
                duration: 5000,
              },
            );
          }
        }),
        map((data) =>
          PaginatedProjectJobActions.listPaginatedProjectJobsSuccess({
            data: {
              ...data,
              items: (data.items ?? []).map(
                (item) =>
                  toJson(
                    ProjectJobServiceCreateResponseSchema,
                    item,
                    defaultApiSerializeJsonOptions,
                  ) as ProjectJobServiceCreateResponseJson,
              ),
            },
          }),
        ),
        catchError((error) => {
          this.errors.reportCaughtError(error);
          return of(
            PaginatedProjectJobActions.listPaginatedProjectJobsFailure(error),
          );
        }),
        tap(() => this.utilities.complete()),
      );
  }

  constructor() {
    const actions$ = inject(Actions);

    super(actions$);
  }
}
